import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ChatThread } from '@/components/conversations/chat-thread';
import { useUIStore } from '@/stores/ui-store';
import { useConversationStore } from '@/stores/conversation-store';
import { useModuleStore } from '@/stores/module-store';
import type { Conversation } from '@/types/messaging';

/**
 * Floating chat dialog shown when the user:
 * - clicks a desktop notification carrying a conversationId,
 * - clicks the chat-icon on an item in the notification panel, or
 * - loads the app with `?openChatId=<id>` in the URL.
 *
 * The dialog shares `activeConversation` / `messages` with the inbox view by
 * calling `setActiveConversation` on open. It does NOT mutate `conversations`
 * — for an old/out-of-filter conv, we resolve a record from the Firestore
 * inbox doc (+ id parsing fallback) so the chat header isn't empty.
 *
 * Mounted once in `<AppLayout>` so it's available across all views.
 */
export function ChatDialog() {
  const conversationId = useUIStore((s) => s.chatDialogConversationId);
  const closeChatDialog = useUIStore((s) => s.closeChatDialog);

  const activeConversation = useConversationStore((s) => s.activeConversation);
  const messages = useConversationStore((s) => s.messages);
  const messagesLoading = useConversationStore((s) => s.messagesLoading);
  const tpaModule = useModuleStore((s) => s.tpaModule);

  const [resolving, setResolving] = useState(false);

  // Snapshot of the inbox's active conversation at the moment the dialog opened.
  // Restored on close so clicking "X" returns the user to whatever chat they
  // had open before (including "nothing selected").
  const previousActiveConvRef = useRef<Conversation | null>(null);
  const wasOpenRef = useRef(false);

  // Track open/close transitions to capture-on-open and restore-on-close.
  useEffect(() => {
    const isOpen = !!conversationId;
    const wasOpen = wasOpenRef.current;

    if (isOpen && !wasOpen) {
      // Opening — snapshot the inbox's current active conversation BEFORE the
      // resolve-effect below overwrites it with the dialog's target.
      previousActiveConvRef.current = useConversationStore.getState().activeConversation;
    } else if (!isOpen && wasOpen) {
      // Closing — restore the previous active conversation (or clear if none).
      const prev = previousActiveConvRef.current;
      const current = useConversationStore.getState().activeConversation;
      if (prev && prev.id !== current?.id) {
        useConversationStore.getState().setActiveConversation(prev);
      } else if (!prev && current) {
        useConversationStore.getState().setActiveConversation(null);
      }
      previousActiveConvRef.current = null;
    }

    wasOpenRef.current = isOpen;
  }, [conversationId]);

  // Resolve + activate the conversation whenever the dialog opens (or the id changes).
  useEffect(() => {
    if (!conversationId || !tpaModule) return;

    let cancelled = false;
    const store = useConversationStore.getState();

    // Fast path — already active.
    if (store.activeConversation?.id === conversationId) return;

    setResolving(true);
    store.resolveConversationById(conversationId)
      .then((conv) => {
        if (cancelled) return;
        // setActiveConversation triggers fetchMessages + fetchActiveConvDetails.
        useConversationStore.getState().setActiveConversation(conv);
        // Inbox-side rule: only fire markAsRead when there's actually
        // an unread badge to clear. Avoids a CF roundtrip every time a
        // chat-dialog reopens an already-read conversation.
        if (conv.unreadCount > 0) {
          useConversationStore.getState().markAsRead(conversationId);
        }
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId, tpaModule]);

  // Escape closes the dialog.
  useEffect(() => {
    if (!conversationId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeChatDialog();
    };
    document.addEventListener('keydown', handler);
    // Lock scroll on body while dialog is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [conversationId, closeChatDialog]);

  if (!conversationId) return null;

  // Only render ChatThread once the active conversation matches what we asked
  // for — otherwise we'd flash the previous conversation's header/messages.
  const conversationReady =
    !resolving && activeConversation?.id === conversationId;

  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeChatDialog}
      />
      {/* Content container */}
      <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none">
        {/* Wrapper is `relative` so the close-X can be absolutely positioned
            just outside the card's rounded top-right corner without being
            clipped by the card's `overflow-hidden`. */}
        <div className="pointer-events-auto relative w-full max-w-3xl" style={{ height: '80vh', maxHeight: '80vh' }}>
          {/* Close button — sits above and just outside the card corner so it
              doesn't collide with the ChatThread header actions (call, lookup
              toggle, overflow menu). */}
          <button
            onClick={closeChatDialog}
            className="absolute -right-2 -top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-md hover:bg-muted transition-colors"
            aria-label="Close chat"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div
            className="relative h-full w-full rounded-xl border border-border bg-background shadow-xl animate-in fade-in-0 zoom-in-95 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
          {conversationReady && activeConversation ? (
            <ChatThread
              conversation={activeConversation}
              messages={messages}
              loading={messagesLoading}
              onBack={closeChatDialog}
              onRefresh={() => useConversationStore.getState().refreshMessages()}
              onMessageSent={() => {
                // No-op: chat-input fetches the sent message by SID and updates
                // the conversation subject directly. Mirrors conversations-view.
              }}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
